"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";

/**
 * Search overrides: query parameters prefixed with "sx." are passed on to the search function as they are,
 * e.g. ?sx.strategy=ExactFirst&sx.debug=1 sends strategy=ExactFirst&debug=1 (SAN-805).
 * The search function decides what they mean (see readme.md in FilmwebSearchFunctions), so testing a new
 * search behaviour only needs a backend change.
 *
 * Overrides are kept in sessionStorage, so they survive navigation. ?sx.reset clears them, an empty value (sx.debug=) removes one.
 * "cs" (custom sort) is handled in the frontend and not sent on.
 *
 * Each search can also have defaults from env (QUICKSEARCH_DEFAULTS, FULLSEARCH_DEFAULTS below). They are sent on every search,
 * and overrides win over them. Defaults are not shown in the "Søketest" banner: hasOverrides is only true for sx.* parameters.
 */
export const SEARCH_OVERRIDE_PREFIX = "sx.";
export const SEARCH_OVERRIDE_STORAGE_KEY = "fw-search-overrides";
export type SearchOverrides = Readonly<Record<string, string>>;
const EMPTY: SearchOverrides = Object.freeze({});

// names the search function could understand. "q" is the search text and is never overridden
const VALID_NAME = /^[a-z][a-z0-9]*$/i;
function isValidName(name: string) {
  return VALID_NAME.test(name) && name.toLowerCase() !== "q";
}

/**
 * Applies the sx.* parameters in a query string to the current overrides
 */
export function applyUrlOverrides(current: SearchOverrides, search: string): SearchOverrides {
  const params = Array.from(new URLSearchParams(search)).filter(([key]) => key.toLowerCase().startsWith(SEARCH_OVERRIDE_PREFIX)).map(([key, value]) => [key.substring(SEARCH_OVERRIDE_PREFIX.length), value.trim()] as const);
  if (params.length === 0) {
    return current;
  }
  const result: Record<string, string> = params.some(([name]) => name.toLowerCase() === "reset") ? {} : {
    ...current
  };
  for (const [name, value] of params) {
    if (!isValidName(name) || name.toLowerCase() === "reset") {
      continue;
    }
    deleteName(result, name);
    if (value) {
      result[name] = value;
    }
  }
  return Object.keys(result).length === 0 ? EMPTY : result;
}

// keys are case insensitive in the search function, keep one of each
function deleteName(result: Record<string, string>, name: string) {
  for (const existing of Object.keys(result)) {
    if (existing.toLowerCase() === name.toLowerCase()) {
      delete result[existing];
    }
  }
}
export function readStoredOverrides(storage: Storage | undefined): SearchOverrides {
  try {
    const stored = storage?.getItem(SEARCH_OVERRIDE_STORAGE_KEY);
    if (!stored) {
      return EMPTY;
    }
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return EMPTY;
    }
    const result = Object.fromEntries(Object.entries(parsed).filter(([name, value]) => isValidName(name) && typeof value === "string" && value !== ""));
    return Object.keys(result).length === 0 ? EMPTY : result;
  } catch {
    return EMPTY;
  }
}
export function writeStoredOverrides(storage: Storage | undefined, overrides: SearchOverrides) {
  try {
    if (Object.keys(overrides).length === 0) {
      storage?.removeItem(SEARCH_OVERRIDE_STORAGE_KEY);
    } else {
      storage?.setItem(SEARCH_OVERRIDE_STORAGE_KEY, JSON.stringify(overrides));
    }
  } catch {
    // private mode or blocked storage: overrides only last for this page
  }
}

/** Removes the sx.* parameters from a query string */
export function removeUrlOverrides(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of Array.from(params.keys())) {
    if (key.toLowerCase().startsWith(SEARCH_OVERRIDE_PREFIX)) {
      params.delete(key);
    }
  }
  const result = params.toString();
  return result ? `?${result}` : "";
}

/** The parameters to send to the search function ("cs" is handled in the frontend) */
export function overridesToSearchParams(overrides: SearchOverrides | undefined): Record<string, string> {
  return Object.fromEntries(Object.entries(overrides ?? {}).filter(([name]) => name.toLowerCase() !== "cs"));
}

/** Custom sort from the overrides (sx.cs=2) */
export function customSortFromOverrides(overrides: SearchOverrides | undefined): string | undefined {
  return Object.entries(overrides ?? {}).find(([name]) => name.toLowerCase() === "cs")?.[1];
}

/**
 * Parses search defaults from env, written like a query string without the sx. prefix, e.g. "strategy=ExactFirst&fuzzy=1".
 * Invalid names and empty values are skipped.
 */
export function parseSearchDefaults(value: string | undefined): SearchOverrides {
  const result: Record<string, string> = {};
  for (const [name, rawValue] of new URLSearchParams(value?.trim() ?? "")) {
    const trimmed = rawValue.trim();
    if (isValidName(name) && trimmed) {
      deleteName(result, name);
      result[name] = trimmed;
    }
  }
  return Object.keys(result).length === 0 ? EMPTY : result;
}

/** The defaults with the overrides on top. An override replaces a default with the same name in any case */
export function mergeSearchOverrides(defaults: SearchOverrides | undefined, overrides: SearchOverrides | undefined): SearchOverrides {
  if (!overrides || Object.keys(overrides).length === 0) {
    return defaults ?? EMPTY;
  }
  if (!defaults || Object.keys(defaults).length === 0) {
    return overrides;
  }
  const result: Record<string, string> = {
    ...defaults
  };
  for (const [name, value] of Object.entries(overrides)) {
    deleteName(result, name);
    result[name] = value;
  }
  return result;
}

/** Defaults for the quick search in the menu (NEXT_PUBLIC_QUICKSEARCH_DEFAULTS in .env-cmdrc) */
export const QUICKSEARCH_DEFAULTS = parseSearchDefaults(process.env.NEXT_PUBLIC_QUICKSEARCH_DEFAULTS);

/** Defaults for the full search on /sok (NEXT_PUBLIC_FULLSEARCH_DEFAULTS in .env-cmdrc) */
export const FULLSEARCH_DEFAULTS = parseSearchDefaults(process.env.NEXT_PUBLIC_FULLSEARCH_DEFAULTS);

//#region [Store]
let currentOverrides: SearchOverrides = EMPTY;
const listeners = new Set<() => void>();
function getSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}
function setOverrides(overrides: SearchOverrides) {
  if (sameOverrides(overrides, currentOverrides)) {
    return;
  }
  currentOverrides = overrides;
  listeners.forEach(listener => listener());
}
function sameOverrides(a: SearchOverrides, b: SearchOverrides) {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every(key => a[key] === b[key]);
}

/** Reads stored overrides and the sx.* parameters in the current URL */
export function refreshSearchOverrides() {
  const storage = getSessionStorage();
  const stored = readStoredOverrides(storage);
  const next = applyUrlOverrides(stored, window.location.search);
  if (next !== stored) {
    writeStoredOverrides(storage, next);
  }
  setOverrides(next);
}

/** Clears the overrides, also from the URL so a reload does not bring them back */
export function resetSearchOverrides() {
  writeStoredOverrides(getSessionStorage(), EMPTY);
  const search = removeUrlOverrides(window.location.search);
  if (search !== window.location.search) {
    window.history.replaceState(window.history.state, "", window.location.pathname + search + window.location.hash);
  }
  setOverrides(EMPTY);
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
const getSnapshot = () => currentOverrides;
const getServerSnapshot = () => EMPTY;
//#endregion

/**
 * The current search overrides. Empty during server rendering and hydration, read from the URL and sessionStorage after mount
 * and on every navigation.
 */
export function useSearchOverrides() {
  const $ = _c(7);
  const pathname = usePathname();
  const overrides = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  let t0;
  if ($[0] !== pathname) {
    t0 = [pathname];
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(_temp, t0);
  let t1;
  if ($[2] !== overrides) {
    t1 = Object.keys(overrides);
    $[2] = overrides;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = t1.length > 0;
  let t3;
  if ($[4] !== overrides || $[5] !== t2) {
    t3 = {
      overrides,
      hasOverrides: t2,
      reset: resetSearchOverrides
    };
    $[4] = overrides;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp() {
  refreshSearchOverrides();
}